/**
 * PAPPO — ConversationList Component
 * Lista de conversas com avatar, nome, última mensagem, timestamp e badge de
 * não-lidas. Mostra o apelido local (alias) no lugar do nome quando existe, uma
 * secção recolhível "Arquivadas (N)" no TOPO (estilo WhatsApp), e grupos de
 * utilizador (group_kind='user') com um botão de gerir (⚙).
 */

import { useState } from 'react';
import { Archive, ChevronDown, ChevronRight } from 'lucide-react';
import type { Conversation } from '../../types/message';
import { Avatar } from '../common/Avatar';
import { Settings2 } from 'lucide-react';
import styles from './ConversationList.module.css';

interface ConversationListProps {
  conversations: Conversation[];
  activeId?: number;
  onSelect: (conversation: Conversation) => void;
  /** IDs de utilizadores online (presença em tempo real) */
  onlineUserIds?: Set<number>;
  /** Abrir o painel de gerir um grupo de utilizador (adicionar/remover/sair) */
  onManageGroup?: (conversation: Conversation) => void;
}

/** Nome a exibir: alias local tem prioridade sobre o display_name real */
function conversationName(conv: Conversation): string {
  if (conv.is_group) return conv.name || 'Família';
  return (
    conv.other_user?.alias?.trim() ||
    conv.other_user?.display_name ||
    'Utilizador'
  );
}

/** Formata timestamp para exibição curta */
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) {
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onlineUserIds,
  onManageGroup,
}: ConversationListProps) {
  const [showArchived, setShowArchived] = useState(false);

  if (conversations.length === 0) {
    return (
      <div className={styles.empty}>
        <p>Nenhuma conversa ainda</p>
        <p className={styles.emptyHint}>Adicione um contacto para começar a conversar</p>
      </div>
    );
  }

  /* Arquivar é per-user e só visual: separa a lista ativa das arquivadas. */
  const active = conversations.filter((c) => !c.archived);
  const archived = conversations.filter((c) => c.archived);

  const renderItem = (conv: Conversation) => {
    const isActive = conv.id === activeId;
    const isGroup = !!conv.is_group;
    const isUserGroup = isGroup && conv.group_kind === 'user';
    const name = conversationName(conv);
    const avatarSrc = isGroup ? conv.avatar_url : conv.other_user?.avatar_url;
    const lastMsg = conv.last_message?.content ?? '';
    const time = conv.last_message?.created_at
      ? formatTime(conv.last_message.created_at)
      : '';
    const otherId = isGroup ? undefined : conv.other_user?.id;
    const isOnline =
      otherId != null && onlineUserIds ? onlineUserIds.has(otherId) : false;

    return (
      <li key={conv.id} style={{ position: 'relative' }}>
        <div
          className={`${styles.item} ${isActive ? styles.active : ''}`}
          onClick={() => onSelect(conv)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(conv);
            }
          }}
          role="option"
          aria-selected={isActive}
          tabIndex={0}
        >
          <Avatar name={name} src={avatarSrc} size="md" online={isOnline} />

          <div className={styles.content}>
            <div className={styles.header}>
              <span className={styles.name}>
                {name}
                {isGroup && (
                  <span
                    style={{
                      marginLeft: 6,
                      padding: '1px 6px',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: 8,
                      background: 'var(--accent-glow, rgba(99,102,241,0.15))',
                      color: 'var(--accent-primary, #6366f1)',
                      verticalAlign: 'middle',
                    }}
                  >
                    {isUserGroup ? 'Grupo' : 'Família'}
                  </span>
                )}
              </span>
              <span className={styles.time}>{time}</span>
            </div>
            <div className={styles.preview}>
              <span className={styles.message}>{lastMsg}</span>
              {(conv.unread_count ?? 0) > 0 && (
                <span className={styles.badge}>{conv.unread_count}</span>
              )}
            </div>
          </div>
        </div>

        {isUserGroup && onManageGroup && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onManageGroup(conv);
            }}
            aria-label="Gerir grupo"
            title="Gerir grupo"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              borderRadius: 6,
              cursor: 'pointer',
              zIndex: 2,
            }}
          >
            <Settings2 size={16} />
          </button>
        )}
      </li>
    );
  };

  return (
    <ul className={styles.list} role="listbox" aria-label="Lista de conversas">
      {/* Secção "Arquivadas (N)" no topo (WhatsApp style) */}
      {archived.length > 0 && (
        <li>
          <button
            type="button"
            className={styles.archivedToggle}
            onClick={() => setShowArchived((v) => !v)}
            aria-expanded={showArchived}
          >
            <Archive size={18} className={styles.archivedIcon} />
            <span className={styles.archivedLabel}>
              Arquivadas ({archived.length})
            </span>
            {showArchived ? (
              <ChevronDown size={16} className={styles.archivedChevron} />
            ) : (
              <ChevronRight size={16} className={styles.archivedChevron} />
            )}
          </button>
        </li>
      )}

      {/* Conversas arquivadas (só quando expandidas) */}
      {showArchived && archived.map(renderItem)}

      {/* Conversas ativas */}
      {active.map(renderItem)}
    </ul>
  );
}
